/**
 * 사용자별 대시보드 배치 — 서버 저장 (2026-09-16 · 대시보드 v2 V10 · 서버 요청 DR1).
 *
 * ★★★ **이 길은 아직 서버에 없을 수 있다.** 깃발 `VITE_DASHBOARD_SERVER_LAYOUT` 이 켜져도
 *     서버가 DR1 을 아직 배포하지 않았으면 404 · 501 이 온다. 그것은 **오류가 아니라 상태**다 —
 *     `unavailable` 로 돌려주고 부르는 쪽은 로컬만으로 계속한다(web-plan §2.2).
 *     그래서 이 모듈은 **던지지 않는다.** 네트워크 실패도 같은 자리로 모은다.
 *
 * ★★ **409 는 실패가 아니다.** 다른 기기가 그새 올렸다는 뜻이고, 서버가 지금 판(`revision`)과
 *    그 본문(`layout`)을 함께 준다 — 부르는 쪽이 그걸로 갈아끼운다. 그래서 `conflict` 를
 *    `ok` 와 같은 급의 결과로 돌려준다.
 *
 * ★ 서버는 **내용을 해석하지 않는다**(DR1). 크기 · 보드 수 · 보드당 위젯 수만 본다.
 *   그래서 이 파일은 `layout` 을 검사하지 않고 그대로 실어 나른다.
 */
import { apiClient } from './client';
import type { DashboardBoard } from '../store/dashboardStore';

/** 서버에 올리는 문서 — 로컬 저장 모양 그대로에서 회사 판만 빼고 `schemaVersion` 을 붙인 것. */
export interface DashboardLayoutDoc {
  /** 서버 쪽 판 번호. 로컬의 `v`(계속 1 · M1)와 **따로** 센다. */
  schemaVersion: number;
  activeBoardId: string;
  /** 회사 판(`kind:'corp'`)은 들어 있지 않다 — 그건 서버가 SSOT 다(M4). */
  boards: DashboardBoard[];
  seenForced?: Record<string, number>;
  hiddenBoardIds?: string[];
  defaultBoardId?: string;
}

export type LayoutFetch =
  | { kind: 'ok'; layout: DashboardLayoutDoc | null; revision: number }
  /** 서버가 이 길을 모르거나(404 · 501) 못 닿았다 — 로컬만으로 계속한다. */
  | { kind: 'unavailable' };

export type LayoutPush =
  | { kind: 'ok'; revision: number }
  /** 그새 다른 기기가 올렸다 — 서버 판과 본문이 함께 온다. */
  | { kind: 'conflict'; revision: number; layout: DashboardLayoutDoc | null }
  | { kind: 'unavailable' };

const PATH = '/users/me/dashboard-layout';

/** 서버가 «아직 없다»고 말하는 상태값들 — 오류로 올리지 않는다. */
function isAbsent(status: number | undefined): boolean {
  return status === 404 || status === 501;
}

function statusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | undefined)?.response?.status;
}

function bodyOf(err: unknown): Record<string, unknown> | undefined {
  return (err as { response?: { data?: Record<string, unknown> } } | undefined)?.response?.data;
}

export async function getDashboardLayout(): Promise<LayoutFetch> {
  try {
    const { data } = await apiClient.get<{ layout: DashboardLayoutDoc | null; revision: number }>(PATH);
    return { kind: 'ok', layout: data?.layout ?? null, revision: Number(data?.revision ?? 0) };
  } catch {
    /* 404 · 501 · 네트워크 · 타임아웃 — 전부 «지금은 못 쓴다» 한 자리로 모은다. */
    return { kind: 'unavailable' };
  }
}

export async function putDashboardLayout(
  layout: DashboardLayoutDoc, baseRevision: number, importedFrom?: string,
): Promise<LayoutPush> {
  try {
    const { data } = await apiClient.put<{ revision: number }>(PATH, { layout, baseRevision, importedFrom });
    return { kind: 'ok', revision: Number(data?.revision ?? baseRevision + 1) };
  } catch (err) {
    const status = statusOf(err);
    if (status === 409) {
      const body = bodyOf(err);
      return {
        kind: 'conflict',
        revision: Number(body?.revision ?? 0),
        layout: (body?.layout as DashboardLayoutDoc | null | undefined) ?? null,
      };
    }
    if (isAbsent(status)) return { kind: 'unavailable' };
    /* 그 밖의 실패(5xx · 네트워크)도 로컬엔 이미 남아 있다 — 다음 기회에 다시 올린다. */
    return { kind: 'unavailable' };
  }
}
